import * as React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Check, ChevronsUpDown } from "lucide-react";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { useFinance } from "@/context/FinanceContext";
import { useToast } from "@/hooks/use-toast";
import { DateInput } from "@/components/ui/date-input";

const schema = z.object({
  paymentDate: z.string().optional(),
  paymentMethodId: z.string().optional(),
  accountId: z.string().optional(),
});

type BulkPaymentFormValues = z.infer<typeof schema>;

interface BulkPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedIds: string[];
  onSubmit: () => void;
}

export const BulkPaymentDialog: React.FC<BulkPaymentDialogProps> = ({
  open,
  onOpenChange,
  selectedIds,
  onSubmit,
}) => {
  const { transactions, bulkUpdateTransactions, accounts, paymentMethods } = useFinance();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = React.useState(false);

  // Estado para capturar valores no momento da abertura do dialog
  const [capturedState, setCapturedState] = React.useState({
    isReversal: false,
    count: 0
  });

  // Calcular estado atual baseado nos lançamentos selecionados
  const currentIsReversal = React.useMemo(() => {
    const selectedTransactions = transactions.filter(t => selectedIds.includes(t.id));
    return selectedTransactions.some(t => t.settled);
  }, [transactions, selectedIds]);

  // Capturar estado ANTES da renderização visual usando useLayoutEffect
  React.useLayoutEffect(() => {
    if (open) {
      setCapturedState({
        isReversal: currentIsReversal,
        count: selectedIds.length
      });
    }
  }, [open, currentIsReversal, selectedIds.length]);

  const form = useForm<BulkPaymentFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      paymentDate: new Date().toISOString().split("T")[0],
      paymentMethodId: "",
      accountId: "",
    },
  });

  // Validação condicional
  const validateForm = (values: BulkPaymentFormValues) => {
    if (!capturedState.isReversal) {
      if (!values.paymentDate) return "Data de pagamento é obrigatória";
      if (!values.paymentMethodId) return "Forma de pagamento é obrigatória"; 
      if (!values.accountId) return "Conta é obrigatória";
    }
    return null;
  };

  const handleSubmit = async (values: BulkPaymentFormValues) => {
    // Validação condicional
    const validationError = validateForm(values);
    if (validationError) {
      console.error(validationError);
      return;
    }

    setIsLoading(true);
    
    // Usar valores capturados do estado
    const wasReversal = capturedState.isReversal;
    const count = capturedState.count;
    
    try {
      if (wasReversal) {
        // Estorna o pagamento
        await bulkUpdateTransactions(selectedIds, {
          paymentDate: "",
          settled: false,
        });
      } else {
        // Confirma o pagamento
        await bulkUpdateTransactions(selectedIds, {
          paymentDate: values.paymentDate!,
          paymentMethodId: values.paymentMethodId!,
          accountId: values.accountId!,
          settled: true,
        });
      }
      
      form.reset();
      
      // Apenas chamar onSubmit (o pai controla o fechamento)
      onSubmit();
      
      // Toast usando valores capturados
      toast({
        title: wasReversal ? "Pagamentos estornados" : "Baixa realizada",
        description: `${count} lançamento(s) ${wasReversal ? 'estornado(s)' : 'baixado(s)'} com sucesso.`,
        duration: 2000,
      });
    } catch (error) {
      console.error('Error in bulk payment:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="medium">
        <DialogHeader>
          <DialogTitle>{capturedState.isReversal ? "Estornar Pagamentos" : "Baixa dos Lançamentos"}</DialogTitle>
          <DialogDescription>
            {capturedState.isReversal 
              ? `Marque os ${capturedState.count} lançamentos selecionados como não pagos.`
              : `Marque os ${capturedState.count} lançamentos selecionados como pagos preenchendo as informações abaixo.`
            }
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          {!capturedState.isReversal && (
            <>
              <div className="space-y-2">
                <Label htmlFor="paymentDate" className="text-sm font-medium text-foreground">
                  Data de Pagamento
                </Label>
                <DateInput
                  value={form.watch("paymentDate")}
                  onChange={(value) => form.setValue("paymentDate", value)}
                  className={cn("w-full", form.formState.errors.paymentDate && "border-destructive focus-visible:ring-destructive")}
                />
                {form.formState.errors.paymentDate && (
                  <div className="text-sm text-destructive">{form.formState.errors.paymentDate.message}</div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="paymentMethodId" className={cn("text-sm font-medium text-foreground", form.formState.errors.paymentMethodId && "text-destructive")}>
                  Forma de Pagamento
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-invalid={!!form.formState.errors.paymentMethodId}
                      className={cn(
                        "w-full justify-between border",
                        !form.watch("paymentMethodId") && "text-muted-foreground",
                        form.formState.errors.paymentMethodId && "border-destructive focus-visible:ring-destructive"
                      )}
                      id="paymentMethodId"
                    >
                      {form.watch("paymentMethodId") ? (
                        paymentMethods.find(pm => pm.id === form.watch("paymentMethodId"))?.name
                      ) : (
                        "Selecione uma forma de pagamento"
                      )}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0">
                    <Command>
                      <CommandInput placeholder="Buscar forma de pagamento..." />
                      <CommandList>
                        <CommandEmpty>Nenhuma forma de pagamento encontrada.</CommandEmpty>
                        <CommandGroup>
                          {paymentMethods.map((method) => (
                            <CommandItem
                              key={method.id}
                              value={`${method.name} ${method.id}`}
                              onSelect={() => {
                                form.setValue("paymentMethodId", method.id, { shouldValidate: true });
                              }}
                            >
                              {method.name}
                              <Check
                                className={cn(
                                  "ml-auto h-4 w-4",
                                  form.watch("paymentMethodId") === method.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {form.formState.errors.paymentMethodId && (
                  <div className="text-sm text-destructive">{form.formState.errors.paymentMethodId.message}</div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="accountId" className={cn("text-sm font-medium text-foreground", form.formState.errors.accountId && "text-destructive")}>
                  Conta Financeira
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-invalid={!!form.formState.errors.accountId}
                      className={cn(
                        "w-full justify-between border",
                        !form.watch("accountId") && "text-muted-foreground",
                        form.formState.errors.accountId && "border-destructive focus-visible:ring-destructive"
                      )}
                      id="accountId"
                    >
                      {form.watch("accountId") ? (
                        accounts.find(a => a.id === form.watch("accountId"))?.name
                      ) : (
                        "Selecione uma conta"
                      )}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0">
                    <Command>
                      <CommandInput placeholder="Buscar conta..." />
                      <CommandList>
                        <CommandEmpty>Nenhuma conta encontrada.</CommandEmpty>
                        <CommandGroup>
                          {accounts.map((account) => (
                            <CommandItem
                              key={account.id}
                              value={`${account.name} ${account.id}`}
                              onSelect={() => {
                                form.setValue("accountId", account.id, { shouldValidate: true });
                              }}
                            >
                              {account.name}
                              <Check
                                className={cn(
                                  "ml-auto h-4 w-4",
                                  form.watch("accountId") === account.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {form.formState.errors.accountId && (
                  <div className="text-sm text-destructive">{form.formState.errors.accountId.message}</div>
                )}
              </div>
            </>
          )}

          {capturedState.isReversal && (
            <div className="p-4 bg-warning/10 border border-warning rounded-md">
              <p className="text-sm text-warning-foreground">
                Os lançamentos selecionados serão marcados como não pagos e as datas de pagamento serão removidas.
              </p>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button 
              type="submit" 
              disabled={isLoading}
              className="min-w-[160px]"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Processando...
                </>
              ) : (
                capturedState.isReversal 
                  ? `Estornar Pagamentos (${capturedState.count} lançamentos)`
                  : `Realizar Baixa (${capturedState.count} lançamentos)`
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
